import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  View,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Modal,
  ScrollView,
} from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { AdminTagChipPicker } from './AdminTagChipPicker';
import type { CorkBoardTag, CorkBoardThreadSummary } from '@/types/residents/corkBoard';

interface EditThreadTagsModalProps {
  visible: boolean;
  thread: CorkBoardThreadSummary | null;
  availableTags: CorkBoardTag[];
  isSaving: boolean;
  onSave: (threadId: string, tagIds: number[]) => void;
  onClose: () => void;
}

export const EditThreadTagsModal: React.FC<EditThreadTagsModalProps> = memo(
  ({ visible, thread, availableTags, isSaving, onSave, onClose }) => {
    const { redesign, semantic } = useAdminColors();
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

    const isAnnouncement = thread?.isAnnouncement ?? false;
    const isSurvey = thread?.kind === 'survey';

    const categoryTags = useMemo(
      () => availableTags.filter((t) => t.slug !== 'announcements' && t.slug !== 'survey'),
      [availableTags],
    );

    const announcementsTagId = useMemo(
      () => availableTags.find((t) => t.slug === 'announcements')?.id ?? null,
      [availableTags],
    );

    const surveyTag = useMemo(
      () => availableTags.find((t) => t.slug === 'survey') ?? null,
      [availableTags],
    );
    const surveyTagId = surveyTag?.id ?? null;

    useEffect(() => {
      if (visible && thread) {
        const tagIds = thread.tags.map((t) => Number(t.id));
        // Strip system tags (announcements / survey) — they are shown as locked
        // and re-applied automatically on save.
        setSelectedTagIds(tagIds.filter((id) => id !== announcementsTagId && id !== surveyTagId));
      }
    }, [visible, thread, announcementsTagId, surveyTagId]);

    const handleToggleTag = useCallback((tagId: number) => {
      setSelectedTagIds((prev) => {
        if (prev.includes(tagId)) {
          return prev.filter((id) => id !== tagId);
        }
        return [tagId];
      });
    }, []);

    const handleSave = useCallback(() => {
      if (isSaving || !thread) return;
      if (!isAnnouncement && !isSurvey && selectedTagIds.length === 0) return;
      const finalTagIds = [...selectedTagIds];
      if (isAnnouncement && announcementsTagId && !finalTagIds.includes(announcementsTagId)) {
        finalTagIds.push(announcementsTagId);
      }
      if (isSurvey && surveyTagId && !finalTagIds.includes(surveyTagId)) {
        finalTagIds.push(surveyTagId);
      }
      // Never submit an empty tag set (the API requires at least one tag).
      if (finalTagIds.length === 0) return;
      onSave(thread.id, finalTagIds);
    }, [isSaving, thread, isAnnouncement, isSurvey, selectedTagIds, announcementsTagId, surveyTagId, onSave]);

    const handleOverlayPress = useCallback(() => {
      if (!isSaving) onClose();
    }, [isSaving, onClose]);

    const isValid = isAnnouncement || isSurvey || selectedTagIds.length === 1;

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={isSaving ? undefined : onClose}>
        <Pressable style={styles.overlay} onPress={handleOverlayPress}>
          <Pressable style={styles.modalWrapper} onPress={(e) => e.stopPropagation()}>
            <GlassCard
              padding={0}
              style={[styles.modalContainer, { backgroundColor: redesign.bg.modal }]}
            >
              <View style={[styles.header, { borderBottomColor: redesign.card.border }]}>
                <View>
                  <Text style={[styles.headerTitle, { color: redesign.text.primary }]}>
                    {isSurvey ? 'Edit Survey Tags' : 'Edit Thread Tags'}
                  </Text>
                  {thread && (
                    <Text
                      style={[styles.headerSubtitle, { color: redesign.text.secondary }]}
                      numberOfLines={1}
                    >
                      {thread.title}
                    </Text>
                  )}
                </View>
                <TouchableOpacity
                  onPress={onClose}
                  disabled={isSaving}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <View style={isSaving ? styles.closeButtonDisabled : undefined}>
                    <SVG_COMPONENTS.CANCEL_X width={18} height={18} color={redesign.text.secondary} />
                  </View>
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                {isAnnouncement && (
                  <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
                    Announcements tag is applied automatically
                  </Text>
                )}
                {isSurvey && (
                  <Text style={[styles.autoTagNote, { color: redesign.text.subtle }]}>
                    Survey tag is applied automatically
                  </Text>
                )}
                <AdminTagChipPicker
                  tags={categoryTags}
                  selectedTagIds={selectedTagIds}
                  onToggleTag={handleToggleTag}
                  lockedTag={isSurvey ? surveyTag : null}
                  label={isAnnouncement || isSurvey ? 'Category tag (optional)' : 'Select a tag'}
                />
              </ScrollView>

              <View style={[styles.footer, { borderTopColor: redesign.card.border }]}>
                <TouchableOpacity
                  style={[styles.cancelButton, { borderColor: redesign.card.border }]}
                  onPress={onClose}
                  disabled={isSaving}
                >
                  <Text style={[styles.cancelButtonText, { color: redesign.text.secondary }]}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.saveButton,
                    { backgroundColor: semantic.interactive.primary },
                    (!isValid || isSaving) && styles.saveButtonDisabled,
                  ]}
                  onPress={handleSave}
                  disabled={!isValid || isSaving}
                >
                  <Text style={styles.saveButtonText}>{isSaving ? 'Saving...' : 'Save'}</Text>
                </TouchableOpacity>
              </View>
            </GlassCard>
          </Pressable>
        </Pressable>
      </Modal>
    );
  },
);

EditThreadTagsModal.displayName = 'EditThreadTagsModal';

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalWrapper: {
    width: '90%',
    maxWidth: 480,
    maxHeight: '80%',
  },
  modalContainer: {
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    gap: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  headerSubtitle: {
    fontSize: 13,
    marginTop: 2,
    maxWidth: 320,
  },
  closeButtonDisabled: {
    opacity: 0.3,
  },
  body: {
    flexGrow: 0,
  },
  bodyContent: {
    padding: 20,
    gap: 12,
  },
  autoTagNote: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
  },
  cancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
